import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Copy, Check, ChevronRight, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import UpvoteControl from '@/components/exchange/UpvoteControl';
import PostCard from '@/components/exchange/PostCard';
import { CategoryChip, GooderaPick } from '@/components/exchange/Chips';
import { fetchPost, fetchPosts, timeAgo } from '@/lib/exchangeApi';

const PostDetailPage = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    fetchPost(id)
      .then(async (p) => {
        if (!active) return;
        setPost(p);
        try {
          const list = await fetchPosts({ category: p.category, sort: 'top' });
          if (active) setRelated(list.filter((x) => x.id !== p.id).slice(0, 3));
        } catch { /* ignore */ }
      })
      .catch(() => active && setNotFound(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [id]);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(post.prompt);
      setCopied(true);
      toast.success('Prompt copied — go paste it in Claude');
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-32"><Loader2 size={28} className="animate-spin text-ink-muted" /></div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="post-not-found">
        <p className="text-ink-secondary mb-4">This prompt could not be found.</p>
        <Link to="/exchange" className="text-brand-orange font-semibold">← Back to the Exchange</Link>
      </div>
    );
  }

  const dateStr = new Date(post.created_at).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="post-detail-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-ink-secondary mb-8">
        <Link to="/exchange" className="hover:text-ink transition-colors">The Exchange</Link>
        <ChevronRight size={13} className="text-ink-muted shrink-0" />
        <span className="text-ink font-medium truncate">{post.title}</span>
      </nav>

      {/* Title + attribution */}
      <div className="flex gap-5 items-start mb-8">
        <UpvoteControl postId={post.id} count={post.upvotes} large />
        <div className="min-w-0">
          <h1 className="text-3xl md:text-4xl font-bold text-ink leading-tight mb-3" data-testid="post-detail-title">
            {post.title}
          </h1>
          <div className="flex items-center flex-wrap gap-x-2 gap-y-2 text-sm text-ink-secondary">
            <span>by <span className="font-semibold text-ink">{post.display_name}</span> · {post.organization} · {dateStr}</span>
            <CategoryChip category={post.category} />
            {post.endorsed && <GooderaPick />}
          </div>
        </div>
      </div>

      {/* What it does */}
      <section className="mb-8">
        <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-brand-orange mb-2">What it does</h2>
        <p className="text-ink leading-relaxed text-[15px]">{post.description}</p>
      </section>

      {/* The prompt */}
      <section className="mb-8">
        <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-brand-orange mb-2">The prompt</h2>
        <div className="rounded-2xl bg-brand-teal/[0.04] border border-brand-teal/15 overflow-hidden">
          <pre className="px-6 py-6 text-sm text-ink leading-relaxed whitespace-pre-wrap font-mono" data-testid="post-detail-prompt">
{post.prompt}
          </pre>
          <div className="flex justify-end px-4 py-3 border-t border-brand-teal/15 bg-white/40">
            <button
              onClick={copyPrompt}
              data-testid="post-copy-btn"
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-colors ${
                copied ? 'bg-brand-teal text-white' : 'bg-brand-orange text-white hover:bg-brand-orange-hover'
              }`}
            >
              {copied ? <Check size={16} strokeWidth={2.5} /> : <Copy size={16} strokeWidth={2.2} />}
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
          </div>
        </div>
      </section>

      {/* How to use it */}
      {post.how_to_use && post.how_to_use.length > 0 && (
        <section className="mb-8">
          <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-brand-orange mb-3">How to use it</h2>
          <ol className="space-y-2.5" data-testid="post-how-to-use">
            {post.how_to_use.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-teal text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-ink leading-relaxed text-[15px]">{step}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Works in */}
      {post.works_in && post.works_in.length > 0 && (
        <section className="mb-10">
          <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-brand-orange mb-2">Works in</h2>
          <div className="flex flex-wrap gap-2">
            {post.works_in.map((w) => (
              <span key={w} className="px-3 py-1.5 rounded-full bg-white border border-black/10 text-xs font-semibold text-ink-secondary">{w}</span>
            ))}
          </div>
        </section>
      )}

      {/* More in category */}
      {related.length > 0 && (
        <section className="mt-14 pt-8 border-t border-black/[0.07]">
          <h2 className="text-lg font-bold text-ink mb-4">More in {post.category}</h2>
          <div className="space-y-3">
            {related.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        </section>
      )}

      <div className="mt-10">
        <Link to="/exchange" className="inline-flex items-center gap-2 text-sm font-semibold text-ink-secondary hover:text-ink transition-colors">
          <ArrowLeft size={16} /> Back to the Exchange
        </Link>
      </div>
    </div>
  );
};

export default PostDetailPage;
